import { FieldTopology, FieldLayoutType, LayoutInterface, FieldEventCallback } from './FieldTypes'
import { FieldParameters } from './FieldParameters'
import { Vector3 } from '../types/Vector3'
import { LineLayout } from './layout/LineLayout'
import { CircleLayout } from './layout/CircleLayout'
import { SpiralLayout } from './layout/SpiralLayout'
import { GridLayout } from './layout/GridLayout'
import { HexLayout } from './layout/HexLayout'
import { HexSphericalLayout } from './layout/HexSphericalLayout'
import { GridSphericalLayout } from './layout/GridSphericalLayout'

export class FieldContext {
  // ==================== CORE PROPERTIES ====================
  
  public topology: FieldTopology
  public layoutType: FieldLayoutType
  public parameters: FieldParameters
  public totalCount: number
  
  // ==================== GLOBAL FIELD STATE ====================
  
  private static _globalInstance: FieldContext | null = null
  private static _fieldAwarenessEnabled: boolean = false
  
  // ==================== LAYOUT MANAGEMENT ====================
  
  private _layoutInstance: LayoutInterface | null = null
  private _layoutCache: Map<string, LayoutInterface> = new Map()
  
  // ==================== CONSTRUCTOR ====================
  
  constructor(
    topology: FieldTopology,
    layoutType: FieldLayoutType,
    parameters?: FieldParameters,
    totalCount: number = 0
  ) {
    this.topology = topology
    this.layoutType = layoutType
    this.parameters = parameters || new FieldParameters()
    this.totalCount = totalCount
    
    // Listen to parameter changes
    this.parameters.onParameterChange(this.onParameterChange.bind(this))
  }
  
  // ==================== GLOBAL FIELD ACCESS ====================
  
  /**
   * Set global field context (for POI access)
   */
  public static setGlobalContext(context: FieldContext | null): void {
    FieldContext._globalInstance = context
  }
  
  /**
   * Get global field context (for POI access)
   */
  public static getGlobalContext(): FieldContext | null {
    return FieldContext._globalInstance
  }
  
  /**
   * Set global field awareness enabled state
   */
  public static setGlobalFieldAwarenessEnabled(enabled: boolean): void {
    FieldContext._fieldAwarenessEnabled = enabled
  }
  
  /**
   * Get global field awareness enabled state
   */
  public static getGlobalFieldAwarenessEnabled(): boolean {
    return FieldContext._fieldAwarenessEnabled
  }
  
  // ==================== LAYOUT FACTORY ====================
  
  /**
   * Get layout instance for current layout type
   */
  public getLayoutInstance(): LayoutInterface {
    if (!this._layoutInstance) {
      this._layoutInstance = this.createLayoutInstance()
    }
    return this._layoutInstance
  }
  
  /**
   * Create layout instance based on layout type
   */
  private createLayoutInstance(): LayoutInterface {
    const cacheKey = this.layoutType
    
    if (this._layoutCache.has(cacheKey)) {
      return this._layoutCache.get(cacheKey)!
    }
    
    let layout: LayoutInterface
    
    switch (this.layoutType) {
      case FieldLayoutType.Line:
        layout = new LineLayout()
        break
      case FieldLayoutType.Circle:
        layout = new CircleLayout()
        break
      case FieldLayoutType.Spiral:
        layout = new SpiralLayout()
        break
      case FieldLayoutType.Grid:
        layout = new GridLayout()
        break
      case FieldLayoutType.Hex:
        layout = new HexLayout()
        break
      case FieldLayoutType.HexSpherical:
        layout = new HexSphericalLayout()
        break
      case FieldLayoutType.GridSpherical:
        layout = new GridSphericalLayout()
        break
      default:
        throw new Error(`Unknown layout type: ${this.layoutType}`)
    }
    
    this._layoutCache.set(cacheKey, layout)
    return layout
  }
  
  // ==================== PARAMETER MANAGEMENT ====================
  
  /**
   * Update field parameters
   */
  public updateParameters(newParams: Partial<FieldParameters>): void {
    this.parameters.updateParameters(newParams)
  }
  
  /**
   * Update single parameter
   */
  public updateParameter(paramName: string, value: any): void {
    this.parameters.updateParameter(paramName, value)
  }
  
  /**
   * Update time for animation
   */
  public updateTime(deltaTime: number): void {
    this.parameters.updateTime(deltaTime)
  }
  
  // ==================== FIELD PROPERTIES ====================
  
  /**
   * Update total count of objects in field
   */
  public updateTotalCount(count: number): void {
    this.totalCount = count
  }
  
  /**
   * Change field layout type
   */
  public changeLayoutType(newLayoutType: FieldLayoutType): void {
    if (this.layoutType !== newLayoutType) {
      this.layoutType = newLayoutType
      this._layoutInstance = null // Force recreation
      
      // Update topology if needed
      const newTopology = this.getTopologyForLayoutType(newLayoutType)
      if (this.topology !== newTopology) {
        this.topology = newTopology
      }
    }
  }
  
  /**
   * Get topology for layout type
   */
  private getTopologyForLayoutType(layoutType: FieldLayoutType): FieldTopology {
    switch (layoutType) {
      case FieldLayoutType.Line:
      case FieldLayoutType.Circle:
      case FieldLayoutType.Spiral:
        return FieldTopology.OneD
      case FieldLayoutType.Grid:
      case FieldLayoutType.Hex:
      case FieldLayoutType.HexSpherical:
      case FieldLayoutType.GridSpherical:
        return FieldTopology.TwoD
      default:
        return FieldTopology.OneD
    }
  }
  
  // ==================== FIELD QUERIES ====================
  
  /**
   * Check if field is 1D
   */
  public is1D(): boolean {
    return this.topology === FieldTopology.OneD
  }
  
  /**
   * Check if field is 2D
   */
  public is2D(): boolean {
    return this.topology === FieldTopology.TwoD
  }
  
  /**
   * Check if field uses curves
   */
  public usesCurves(): boolean {
    return this.layoutType === FieldLayoutType.Line ||
           this.layoutType === FieldLayoutType.Circle ||
           this.layoutType === FieldLayoutType.Spiral
  }
  
  /**
   * Check if field uses grid
   */
  public usesGrid(): boolean {
    return this.layoutType === FieldLayoutType.Grid ||
           this.layoutType === FieldLayoutType.Hex ||
           this.layoutType === FieldLayoutType.HexSpherical ||
           this.layoutType === FieldLayoutType.GridSpherical
  }
  
  // ==================== FIELD CALCULATIONS ====================
  
  /**
   * Calculate field bounds
   */
  public calculateFieldBounds(): { min: Vector3; max: Vector3; center: Vector3 } {
    const layout = this.getLayoutInstance()
    const positions: Vector3[] = []
    
    // Sample positions to calculate bounds
    const sampleCount = Math.min(this.totalCount, 100)
    for (let i = 0; i < sampleCount; i++) {
      const index = Math.floor((i / sampleCount) * this.totalCount)
      const position = layout.computePosition(index, this.totalCount, this.parameters)
      positions.push(position)
    }
    
    if (positions.length === 0) {
      return {
        min: new Vector3(),
        max: new Vector3(),
        center: new Vector3()
      }
    }
    
    // Initialize bounds with first position values (avoid modifying original)
    const min = new Vector3(positions[0].x, positions[0].y, positions[0].z)
    const max = new Vector3(positions[0].x, positions[0].y, positions[0].z)
    
    for (const pos of positions) {
      min.min(pos)
      max.max(pos)
    }
    
    // Calculate center without over-engineering
    const center = new Vector3(
      (min.x + max.x) / 2,
      (min.y + max.y) / 2,
      (min.z + max.z) / 2
    )
    
    return { min, max, center }
  }
  
  /**
   * Calculate field size
   */
  public calculateFieldSize(): number {
    const bounds = this.calculateFieldBounds()
    return bounds.max.distanceTo(bounds.min)
  }
  
  // ==================== PRESETS ====================
  
  /**
   * Apply breathing spiral preset
   */
  public applyBreathingSpiralPreset(): void {
    this.changeLayoutType(FieldLayoutType.Spiral)
    this.parameters.applyBreathingPreset()
  }
  
  /**
   * Apply wind effect preset
   */
  public applyWindEffectPreset(): void {
    this.parameters.applyWindPreset()
  }
  
  /**
   * Apply ripple grid preset
   */
  public applyRippleGridPreset(): void {
    this.changeLayoutType(FieldLayoutType.Grid)
    this.parameters.applyRipplePreset()
  }
  
  /**
   * Apply snake line preset
   */
  public applySnakeLinePreset(): void {
    this.changeLayoutType(FieldLayoutType.Line)
    this.parameters.applySnakePreset()
  }
  
  // ==================== FORMATION CREATION ====================
  
  /**
   * Create formation and update field context
   */
  public createFormation(type: 'circle' | 'line' | 'grid' | 'spiral' | 'hex'): void {
    const layoutType = this.getLayoutTypeFromFormation(type)
    const topology = this.getTopologyForLayoutType(layoutType)
    
    this.changeLayoutType(layoutType)
    
    // Update topology if needed
    if (this.topology !== topology) {
      this.topology = topology
    }
    
    //console.log(`Created ${type} formation`)
  }
  
  /**
   * Get layout type from formation name
   */
  private getLayoutTypeFromFormation(formation: string): FieldLayoutType {
    switch (formation) {
      case 'circle': return FieldLayoutType.Circle
      case 'line': return FieldLayoutType.Line
      case 'grid': return FieldLayoutType.Grid
      case 'spiral': return FieldLayoutType.Spiral
      case 'hex': return FieldLayoutType.Hex
      default: return FieldLayoutType.Circle
    }
  }
  
  // ==================== SYSTEM STATUS ====================
  
  /**
   * Get system status for debugging/UI
   */
  public getSystemStatus(): any {
    return {
      layoutType: this.layoutType,
      topology: this.topology,
      totalCount: this.totalCount,
      fieldAwarenessEnabled: FieldContext.getGlobalFieldAwarenessEnabled(),
      isValid: this.isValid(),
      parameters: {
        cellSpacing: this.parameters.cellSpacing,
        hoverAmplitude: this.parameters.hoverAmplitude,
        hoverFrequency: this.parameters.hoverFrequency,
        organicVariation: this.parameters.organicVariation
      }
    }
  }
  
  /**
   * Log system status
   */
  public logSystemStatus(): void {
    console.log('Field Context Status:', this.getSystemStatus())
  }
  
  // ==================== STATIC FACTORY METHODS ====================
  
  /**
   * Create field context with formation
   */
  public static createWithFormation(
    formation: 'circle' | 'line' | 'grid' | 'spiral' | 'hex',
    totalCount: number = 0
  ): FieldContext {
    const context = new FieldContext(
      FieldTopology.TwoD, // Will be corrected in createFormation
      FieldLayoutType.Grid, // Will be corrected in createFormation
      new FieldParameters(),
      totalCount
    )
    
    context.createFormation(formation)
    return context
  }
  
  /**
   * Create breathing spiral field context
   */
  public static createBreathingSpiral(totalCount: number = 10): FieldContext {
    const context = FieldContext.createWithFormation('spiral', totalCount)
    context.applyBreathingSpiralPreset()
    return context
  }
  
  /**
   * Create ripple grid field context
   */
  public static createRippleGrid(totalCount: number = 25): FieldContext {
    const context = FieldContext.createWithFormation('grid', totalCount)
    context.applyRippleGridPreset()
    return context
  }

  // ==================== EVENT HANDLING ====================
  
  /**
   * Handle parameter changes
   */
  private onParameterChange(change: any): void {
    // React to specific parameter changes
    if (change.parameterName === 'gridSize' && this.layoutType === FieldLayoutType.Grid) {
      // Recalculate total count for grid
      this.updateTotalCount(this.parameters.gridSize * this.parameters.gridSize)
    }
  }
  
  
  // ==================== VALIDATION ====================
  
  /**
   * Validate field context
   */
  public validate(): string[] {
    const errors: string[] = []
    
    if (this.totalCount < 0) {
      errors.push('Total count cannot be negative')
    }
    
    errors.push(...this.parameters.validate())
    
    return errors
  }
  
  /**
   * Check if field context is valid
   */
  public isValid(): boolean {
    return this.validate().length === 0
  }
} 